import { Regex, Sort, Point } from '@models/index';

interface MOSSummaryDataObject {
    mosData: number[];
    totalCalls: number;
    traffic: number;
}

export enum MosQuality {
    Poor = 'POOR',
    Satisfactory = 'SATISFACTORY',
    Good = 'GOOD'
}

export function getHourStart(date: Date): number {
    const hour = new Date(date);
    hour.setMinutes(0, 0, 0);
    return hour.getTime();
}

export function getSummaryWindowStart(hours: number): number {
    const start = new Date();
    start.setMinutes(0, 0, 0);
    start.setHours(start.getHours() - (hours - 1));
    return start.getTime();
}

export class MOSSummaryData {
    private _mosData: Point[][]; // 6 different bands of Mos values e.g. 'Mos 1.0 - 2.6'
    private _totalCalls: Point[];
    private _traffic: Point[];

    constructor() {
        this._mosData = [[], [], [], [], [], []];
        this._totalCalls = [];
        this._traffic = [];
    }

    public updateMOSData(mosData: number[], equipment: number, totalCalls: number, traffic: number, date: Date) {
        for (let i = 0; i < this.mosData.length; i++) {
            this.upsertHourlyPoint(this.mosData[i], date, mosData[i]);
        }
        this.upsertHourlyPoint(this.totalCalls, date, totalCalls);
        this.upsertHourlyPoint(this.traffic, date, traffic);
    }

    private upsertHourlyPoint(series: Point[], date: Date, y: number) {
        const hour = getHourStart(date);
        const index = series.findIndex(e => getHourStart(e.x) === hour);

        if (index !== -1) {
            if (series[index].x.getTime() <= date.getTime()) {
                series[index] = { x: date, y: y };
            }
            return;
        }

        series.push({ x: date, y: y });
        Sort.sortDates(series);
        while (series.length > 24) {
            series.shift();
        }
    }

    /**
     * Getter mosData
     * @return {Point[][]}
     */
    public get mosData(): Point[][] {
        return this._mosData;
    }

    /**
     * Getter totalCalls
     * @return {Point[]}
     */
    public get totalCalls(): Point[] {
        return this._totalCalls;
    }

    /**
     * Getter totalCalls
     * @return {Point[]}
     */
    public get traffic(): Point[] {
        return this._traffic;
    }

    public getMOSDataByHours(hours: number) {
        const mosData = new Array(6).fill(0);
        let traffic = 0;
        let totalCalls = 0;

        // sum the points within the rolling x hour window
        const windowStart = getSummaryWindowStart(hours);
        const inWindow = (e: Point) => e.x.getTime() >= windowStart;

        for (let i = 0; i < this.mosData.length; i++) {
            mosData[i] = this.mosData[i].filter(inWindow).reduce((sum, e) => sum + e.y, 0);
        }
        traffic = this.traffic.filter(inWindow).reduce((sum, e) => sum + e.y, 0);
        totalCalls = this.totalCalls.filter(inWindow).reduce((sum, e) => sum + e.y, 0);

        return {
            mosData: mosData,
            totalCalls: totalCalls,
            traffic: traffic
        };
    }

    public getGoodArrayByHours(hours: number) {
        const mosGoodByHours: Point[] = [];

        if (this.mosData[4].length == this.mosData[5].length) {
            const endIndex = hours > this.mosData[4].length ? this.mosData[4].length : hours;
            // need to combine this.mosData[4] and this.mosData[5];
            for (let i = 0; i < endIndex; i++) {
                if (this.mosData[4][i].x.getTime() == this.mosData[5][i].x.getTime()) {
                    mosGoodByHours.push({
                        x: this.mosData[4][i].x,
                        y: this.mosData[4][i].y + this.mosData[5][i].y
                    });
                }
            }
        }

        return mosGoodByHours;
    }

    public getSatisfactoryArrayByHours(hours: number) {
        const mosSatisfactoryByHours: Point[] = [];

        const endIndex = hours > this.mosData[3].length ? this.mosData[3].length : hours;
        // need to combine this.mosData[4] and this.mosData[5];
        for (let i = 0; i < endIndex; i++) {
            mosSatisfactoryByHours.push({
                x: this.mosData[3][i].x,
                y: this.mosData[3][i].y
            });
        }

        return mosSatisfactoryByHours;
    }

    public getPoorArrayByHours(hours: number) {
        const mosPoorByHours: Point[] = [];

        if (this.mosData[0].length == this.mosData[1].length && this.mosData[0].length == this.mosData[2].length) {
            const endIndex = hours > this.mosData[0].length ? this.mosData[0].length : hours;
            // need to combine this.mosData[0] and this.mosData[1] and this.mosData[2];
            for (let i = 0; i < endIndex; i++) {
                if (
                    this.mosData[0][i].x.getTime() == this.mosData[1][i].x.getTime() &&
                    this.mosData[0][i].x.getTime() == this.mosData[2][i].x.getTime()
                ) {
                    mosPoorByHours.push({
                        x: this.mosData[4][i].x,
                        y: this.mosData[0][i].y + this.mosData[1][i].y + this.mosData[2][i].y
                    });
                }
            }
        }
        return mosPoorByHours;
    }
}
